import { useLayoutEffect, type RefObject } from 'react';

/**
 * Caps an open drop-down (a menu opening below its button) so it ends 8px above whichever comes first: the bottom of
 * the visible viewport or the bottom of the nearest ancestor that clips it (a card with overflow hidden). The element
 * scrolls inside itself (it needs overflow-y-auto), so every item stays reachable on a landscape phone (320–430px
 * tall) where a viewport-based cap such as `100dvh - 7rem` still ran past the screen or the card and cut items off.
 * Re-measured on resize/rotation and when the visual viewport changes; it never makes a menu taller than its content.
 */
export function useFitBelow(ref: RefObject<HTMLElement | null>, open: boolean): void {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!open || !el) return;
    const margin = 8;
    const apply = () => {
      el.style.maxHeight = '';
      const vv = window.visualViewport;
      let limit = vv ? vv.offsetTop + vv.height : window.innerHeight;
      if (getComputedStyle(el).position !== 'fixed') {
        for (let a = el.parentElement; a && a !== document.body && a !== document.documentElement; a = a.parentElement) {
          if (/^(hidden|clip|auto|scroll)$/.test(getComputedStyle(a).overflowY)) limit = Math.min(limit, a.getBoundingClientRect().bottom);
        }
      }
      const room = Math.floor(limit - margin - el.getBoundingClientRect().top);
      if (room > 0 && el.scrollHeight > room) el.style.maxHeight = `${room}px`;
    };
    apply();
    window.addEventListener('resize', apply);
    window.visualViewport?.addEventListener('resize', apply);
    return () => {
      window.removeEventListener('resize', apply);
      window.visualViewport?.removeEventListener('resize', apply);
      el.style.maxHeight = '';
    };
  }, [ref, open]);
}
